import React, { useState, useEffect } from "react";
import badgesBg from "assets/images/others/host-side-images/badgesBg.png";
import { ReactComponent as AddCircle } from "assets/images/svgs/add-circle.svg";
import { ReactComponent as EditIcon } from "assets/images/svgs/edit.svg";
import { ReactComponent as DeleteIcon } from "assets/images/svgs/deleteSvg.svg";
import AddRepresentativeModal from "./component/AddRepresentativeModal";
import UpdateRepresentativeModal from "./component/UpdateRepresentativeModal";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { RegisterRsvpDTO } from "utils/helpers/models/host-dtos/register-rsvp.dto";
import EditProfileBg from "assets/images/others/host-side-images/editProfileUSer.png";
import useHost from "../useHooks";
import { siteRoutes } from "utils/helpers/enums/routes";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import { RsvpDTO } from "utils/helpers/models/shared/rsvp.dto";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useStore from "hooks/useStore";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

const MyRepresentatives: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.home },
    { title: "My Representatives", path: siteRoutes.myRepresentatives },
  ];
  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedRepresentative, setSelectedRepresentative] =
    useState<RegisterRsvpDTO | null>(null);
  const [data, setData] = useState<RsvpDTO[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { getRsvps, deleteRsvp } = useHost();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  useEffect(() => {
    getRsvps(setData);
  }, []);

  // Mark initial load as complete when loading finishes
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const openAddModal = () => setIsAddModalOpen(true);

  const openUpdateModal = (representative: RegisterRsvpDTO) => {
    setSelectedRepresentative(representative);
    setIsUpdateModalOpen(true);
  };

  const closeModals = () => {
    setIsAddModalOpen(false);
    setIsUpdateModalOpen(false);
    setSelectedRepresentative(null);
  };

  const handleDelete = async (id: number) => {
    const result = await confirmationPopup("Are you sure you want to delete?");
    if (result.isConfirmed) {
      deleteRsvp(id, setData);
    }
  };

  return (
    <div className="container min-h-screen">
      <Breadcrumb links={breadcrumbLinks} />
      <div className="flex flex-wrap justify-between items-center gap-4 my-4">
        <h2 className="text-2xl font-bold text-blackie">My Representatives</h2>
        <button
          onClick={openAddModal}
          className="flex gap-1 bg-borderColor items-center px-4 py-2 rounded-full text-primaryBlueText"
        >
          <span>
            <AddCircle />
          </span>
          Add Representative
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-4 py-5">
        {data.length > 0
          ? data.map((item: any) => (
              <div
                key={item.id}
                className="relative flex flex-col items-center justify-center p-4 rounded-lg w-[235px] h-[260px] group"
                style={{ backgroundImage: `url(${badgesBg})` }}
              >
                {/* Action Icons */}
                <div className="absolute top-2 right-2 flex gap-2 transition-opacity duration-300">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openUpdateModal(item);
                    }}
                    className="p-2 bg-gray-300 rounded-full shadow-lg hover:bg-gray-400 transition-colors duration-200"
                    title="Edit Representative"
                  >
                    <EditIcon className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    className="p-2 bg-gray-300 rounded-full shadow-lg hover:bg-gray-400 transition-colors duration-200"
                    title="Delete Representative"
                  >
                    <DeleteIcon className="w-4 h-4 text-red-600" />
                  </button>
                </div>

                {/* Profile Content */}
                <div
                  onClick={() => openUpdateModal(item)}
                  className="flex flex-col items-center justify-center cursor-pointer w-full h-full"
                >
                  <div className="hover:scale-110 transition-transform duration-300">
                    <img
                      className="w-[98px] h-[98px] rounded-full"
                      src={
                        item.profilePicUrl?.fileDownloadUri
                          ? item.profilePicUrl.fileDownloadUri
                          : EditProfileBg
                      }
                      alt={"Representative"}
                    />
                  </div>
                  <span className="text-base font-semibold mt-2 text-blackie">
                    {item.firstName || ""} {item.lastName || ""}
                  </span>
                  <span className="text-sm mt-1 text-labelTextColor">
                    {item.email}
                  </span>
                </div>
              </div>
            ))
          : null}
      </div>

      <CustomMessageDisplay
        show={!isLoading && !data.length && !isInitialLoad}
        title="No Representatives Available"
        slogan="You haven't added any representatives yet. Click 'Add Representative' to get started and manage your team members."
        image={noFoundGif}
        imageAlt="No representatives found"
        className="py-12"
        imageClassName="w-24 h-24"
        titleClassName="text-2xl font-bold text-blackie"
        sloganClassName="text-gray-500 text-base"
      />

      {/* Add Modal */}
      {isAddModalOpen && (
        <AddRepresentativeModal
          isOpen={isAddModalOpen}
          onClose={closeModals}
          onAddComplete={() => getRsvps(setData)}
        />
      )}

      {/* Update Modal */}

      {isUpdateModalOpen && (
        <UpdateRepresentativeModal
          isOpen={isUpdateModalOpen}
          onClose={closeModals}
          representative={selectedRepresentative}
          onUpdateComplete={() => getRsvps(setData)}
        />
      )}
    </div>
  );
};

export default MyRepresentatives;
